import { wrapWithEventPropertiesPrefix } from '@adsk/exo-skeleton-api/internal';

import { EventContext } from '../monitoring';
import { getDTMonitoringContext } from '../monitoringContext';

interface DynatraceEvent {
  scope?: string;
  context?: EventContext;
}

const IS_DT_EVENT_MODIFIER_REGISTERED_KEY = 'IS_DT_EVENT_MODIFIER_REGISTERED';

declare global {
  interface Window {
    [IS_DT_EVENT_MODIFIER_REGISTERED_KEY]: boolean;
  }
}

//This function is used to spread the scope property value to higher level since dynatrace doesn't support nested object
function spreadScopedPropertiesForRootLevel(context: EventContext) {
  if (!context.scope) {
    return context;
  }

  const scope = context.scope;

  // We add custom property with scope as a key in report api - https://git.autodesk.com/BIM360/acs-sc-web-platform/blob/master/libs/exo-skeleton-api/monitoring.ts#L77
  // We need to spread the scope property value to higher level since dynatrace doesn't support nested object
  const dynatraceContext: EventContext = {
    ...context,
    ...(context?.[scope] || {}),
  };

  // We need to remove scope field from context to avoid duplication once they will support objects (currently doesn't impact anything)
  if (scope && context.hasOwnProperty(scope)) {
    delete dynatraceContext[scope]; //:TODO: Remove scope field after spread value, currently don't work
  }

  return dynatraceContext;
}

export const addEventModifier = (event: Record<string, unknown>) => {
  try {
    const result = {
      ...event,
      ...wrapWithEventPropertiesPrefix({
        ...getDTMonitoringContext(),
      }),
    };

    return result;
  } catch (e) {
    console.error(`Failed to use dynatrace addEventModifier: ${e}`);
    return event;
  }
};

export function sendEventToDynatrace({ scope, context = {} }: DynatraceEvent) {
  try {
    if (!window?.dynatrace) {
      return;
    }

    // make sure we register it only once
    if (!window[IS_DT_EVENT_MODIFIER_REGISTERED_KEY]) {
      window[IS_DT_EVENT_MODIFIER_REGISTERED_KEY] = true;

      // event modifier add contextual attributes to OOTB and custom events that Dynatrace detects
      // event represents the Dynatrace Event created
      // context represents the JavaScript Event that triggered the Dynatrace Event
      window.dynatrace.addEventModifier(addEventModifier);
    }

    const dynatraceContext: EventContext =
      spreadScopedPropertiesForRootLevel(context);

    window.dynatrace.sendEvent({
      ...wrapWithEventPropertiesPrefix({ scope }),
      ...dynatraceContext,
    });
  } catch (e) {
    console.error(`Failed to send event to Dynatrace: ${e}`);
  }
}
